
import { Component, Vue, Prop } from "vue-property-decorator";
import { Documents } from "@/hooks";
import Docs from "@/store/modules/Documents";
import logger from "@/plugins/logger";
import { PsiraSpecializedQualification } from "models";

@Component
export default class PsiraSpecializedQualifications extends Vue {
  // --- Props ---
  @Prop({ required: true }) aroId!: number;

  // --- Constants ---
  QUALIFICATION_TYPES = {
    EVENT_SECURITY: "Event Security",
    RETAIL_SECURITY: "Retail Security",
    RESPONSE_SECURITY: "Response Security",
    NKP_SECURITY: "National Key Point Security",
    CASH_IN_TRANSIT: "Cash in Transit Security",
    PRIVATE_INVESTIGATION: "Private Investigation Security",
    CCTV_CONTROL_ROOM: "CCTV and Control Room Operator",
    CLOSE_PROTECTION: "Close Protection Officer",
    DOG_HANDLER: "Dog Handler",
    MEDICAL_FIRST_AID: "Medical or First Aid",
    ADVANCED_DRIVING: "Advanced Driving",
    BASIC_FIREFIGHTING: "Basic Firefighting",
  };

  DOG_HANDLER_LEVELS = {
    DH1: "DH1 - Care for a service dog",
    DH2: "DH2 - Handle a trained service dog to deter crime",
    DH3: "DH3 - Supervise kennel practices",
    DH4: "DH4 - Handle a patrol dog to assist in searching for and the apprehension of a suspect",
    DH5: "DH5 - Utilize a tracker dog to follow a human scent trail",
    DH6: "DH6 - Handle a trained sniffer dog to assist in the detection of substances",
  };

  CLOSE_PROTECTION_OPTIONS = [
    "Provide static protection to designated persons U/S 244317",
    "Provide close protection to designated persons while in transit U/S 244319",
    "Provide pedestrian escort to designated persons within a close protection environment U/S 244327",
    "Compile a threat and risk assessment for a close protection operation U/S 244330",
    "Provide close protection to a designated person whilst embus and debus U/S 244334",
    "Explain the requirements of becoming a security service provider U/S 246694",
    "Demonstrate physical defensive restraining techniques U/S 120486",
  ];

  CLOSE_PROTECTION_OPTION_FIELD_MAP = {
    "Provide static protection to designated persons U/S 244317":
      "provideStaticProtection",
    "Provide close protection to designated persons while in transit U/S 244319":
      "provideCloseProtectionInTransit",
    "Provide pedestrian escort to designated persons within a close protection environment U/S 244327":
      "providePedestrianEscort",
    "Compile a threat and risk assessment for a close protection operation U/S 244330":
      "compileThreatAndRiskAssessment",
    "Provide close protection to a designated person whilst embus and debus U/S 244334":
      "provideCloseProtectionInEmbusDebus",
    "Explain the requirements of becoming a security service provider U/S 246694":
      "explainTheRequirementsOfBecomingSecurity",
    "Demonstrate physical defensive restraining techniques U/S 120486":
      "demonstrateRestrainingTechniques",
  };

  // --- State ---
  hoveredCard: number | null = null;
  selectedQualificationOptions: string[] = [];
  valid = false;
  showAddDialog = false;
  confirmDialog = false;
  dateMenu = false;
  selectedQualificationType = "";
  certificate: File | null = null;
  trainingProvider = "";
  dateCompleted = "";
  qualificationAchieved = "";
  sassetaAccredited = false;
  selectedDogHandlerLevel = "";
  selectedCloseProtectionOptions: string[] = [];
  editingQualification: PsiraSpecializedQualification | null = null;
  qualificationToDelete: PsiraSpecializedQualification | null = null;
  certificateHasExpiry = false;
  certificateExpiryDate = "";
  expiryDateMenu = false;

  // --- Computed ---
  get qualifications(): PsiraSpecializedQualification[] {
    if (Docs.getAroDocs && Docs.getAroDocs.psiraSpecializedQualifications) {
      return Docs.getAroDocs.psiraSpecializedQualifications;
    }
    return [];
  }
  get qualificationTypeOptions(): string[] {
    return Object.values(this.QUALIFICATION_TYPES);
  }
  get dogHandlerLevelOptions(): string[] {
    return Object.values(this.DOG_HANDLER_LEVELS);
  }
  get needsDateAndQualification(): boolean {
    return [
      this.QUALIFICATION_TYPES.MEDICAL_FIRST_AID,
      this.QUALIFICATION_TYPES.ADVANCED_DRIVING,
      this.QUALIFICATION_TYPES.BASIC_FIREFIGHTING,
    ].includes(this.selectedQualificationType);
  }
  get needsSassetaAccreditation(): boolean {
    return [
      this.QUALIFICATION_TYPES.CLOSE_PROTECTION,
      this.QUALIFICATION_TYPES.MEDICAL_FIRST_AID,
      this.QUALIFICATION_TYPES.ADVANCED_DRIVING,
      this.QUALIFICATION_TYPES.BASIC_FIREFIGHTING,
    ].includes(this.selectedQualificationType);
  }

  // --- Methods ---
  /**
   * Format a date string to locale date.
   */
  formatDate(dateString: string): string {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  }

  /**
   * Populate form for editing a qualification.
   */
  editQualification(qualification: PsiraSpecializedQualification): void {
    this.editingQualification = qualification;
    this.selectedQualificationType = qualification.qualificationType;
    this.trainingProvider = qualification.trainingProvider;

    if (qualification.expiryDate) {
      this.certificateHasExpiry = true;
      this.certificateExpiryDate = new Date(qualification.expiryDate)
        .toISOString()
        .substr(0, 10);
    } else {
      this.certificateHasExpiry = false;
      this.certificateExpiryDate = "";
    }

    if (qualification.dateCompleted) {
      this.dateCompleted = new Date(qualification.dateCompleted)
        .toISOString()
        .substr(0, 10);
    }
    this.qualificationAchieved = qualification.qualificationAchieved || "";
    this.sassetaAccredited = qualification.sassetaAccredited || false;
    if (qualification.dogHandlerLevel) {
      this.selectedDogHandlerLevel = qualification.dogHandlerLevel;
    }
    if (qualification.closeProtectionOptions) {
      this.selectedCloseProtectionOptions =
        qualification.closeProtectionOptions || [];
    }
    this.showAddDialog = true;

    this.selectedCloseProtectionOptions = [];

    // Check each option field and add to selectedCloseProtectionOptions if true
    if (
      qualification.qualificationType ===
      this.QUALIFICATION_TYPES.CLOSE_PROTECTION
    ) {
      // Reverse map from field names to option strings
      const reverseMap = {};
      Object.entries(this.CLOSE_PROTECTION_OPTION_FIELD_MAP).forEach(
        ([option, field]) => {
          reverseMap[field] = option;
        }
      );

      // Add each enabled option
      Object.entries(qualification).forEach(([key, value]) => {
        if (reverseMap[key] && value === true) {
          this.selectedCloseProtectionOptions.push(reverseMap[key]);
        }
      });
    }
  }

  /**
   * Ask for confirmation before deleting a qualification.
   */
  deleteQualification(qualification: PsiraSpecializedQualification): void {
    this.qualificationToDelete = qualification;
    this.confirmDialog = true;
  }

  /**
   * Confirm and delete the selected qualification.
   */
  async confirmDeleteQualification(): Promise<void> {
    if (this.qualificationToDelete) {
      try {
        await Documents.deleteAllPSIRASpecializedQualification(
          this.qualificationToDelete.id
        );
        this.$emit("refresh");
        this.confirmDialog = false;
        this.qualificationToDelete = null;
      } catch (error) {
        logger.error("Error deleting qualification:", error);
      }
    }
  }

  /**
   * Save (add or edit) a qualification.
   */
  async saveQualification(): Promise<void> {
    if (!(this.$refs.form as any).validate()) {
      return;
    }
    try {
      const formData = new FormData();
      // Common fields
      formData.append(
        "PSIRASpecializedQualificationAROID",
        this.aroId.toString()
      );
      formData.append(
        "SpecializedEventSecurityType",
        this.selectedQualificationType
      );
      formData.append(
        "SpecializedEventTrainingProvider",
        this.trainingProvider
      );
      if (this.certificateHasExpiry && this.certificateExpiryDate) {
        formData.append("ExpiryDate", this.certificateExpiryDate);
      } else {
        formData.append("ExpiryDate", ""); // send empty string for null value
      }
      // Certificate file
      if (this.certificate) {
        formData.append("PSIRASpecializedEventFile", this.certificate);
        formData.append("SpecializedEventUploadDocument", "true");
      }
      // Date and qualification fields
      if (this.needsDateAndQualification) {
        formData.append("PSIRASpecializedEventExpiryDate", this.dateCompleted);
        formData.append("QualificationAchieved", this.qualificationAchieved);
      }
      // SASSETA accreditation
      if (this.needsSassetaAccreditation) {
        formData.append("SassetaAccredited", this.sassetaAccredited.toString());
      }
      // Dog Handler specific fields
      if (
        this.selectedQualificationType === this.QUALIFICATION_TYPES.DOG_HANDLER
      ) {
        formData.append("DogHandlerType", this.selectedDogHandlerLevel);
      }
      // Close Protection specific fields
      if (
        this.selectedQualificationType ===
          this.QUALIFICATION_TYPES.CLOSE_PROTECTION &&
        this.sassetaAccredited
      ) {
        // Process all options in one go - create a Set of selected options for fast lookup
        const selectedOptionSet = new Set(this.selectedCloseProtectionOptions);

        // Set each option's value based on whether it's in the selected set
        Object.entries(this.CLOSE_PROTECTION_OPTION_FIELD_MAP).forEach(
          ([option, fieldName]) => {
            const isSelected = selectedOptionSet.has(option);
            formData.append(fieldName, isSelected ? "true" : "false");
          }
        );

        // Keep the options array for UI purposes
        formData.append(
          "CloseProtectionOptions",
          JSON.stringify(this.selectedCloseProtectionOptions)
        );
      }
      // For editing existing qualification
      if (this.editingQualification) {
        formData.append("ID", this.editingQualification.id.toString());
      }
      await Documents.addPSIRASpecializedQualification(formData);
      this.resetForm();
      this.$emit("refresh");
    } catch (error) {
      logger.error("Error saving qualification:", error);
    }
  }

  /**
   * Reset the add/edit form.
   */
  resetForm(): void {
    this.showAddDialog = false;
    this.selectedQualificationType = "";
    this.certificate = null;
    this.trainingProvider = "";
    this.dateCompleted = "";
    this.qualificationAchieved = "";
    this.sassetaAccredited = false;
    this.selectedDogHandlerLevel = "";
    this.selectedCloseProtectionOptions = [];
    this.editingQualification = null;
    this.certificateHasExpiry = false;
    this.certificateExpiryDate = "";
    (this.$refs.form as any)?.reset();
  }

  /**
   * Open the certificate file in a new tab.
   */
  async viewCertificate(filePath: string): Promise<void> {
    if (!filePath) return;
    try {
      const file = await Documents.getFile(filePath);
      if (!file) {
        this.$toast?.error?.("File not found or could not be loaded.");
        return;
      }
      // Create a blob URL and open in new tab
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      this.$toast?.error?.("Failed to load certificate.");
    }
  }

  isExpired(dateString: string): boolean {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const today = new Date();
    return expiryDate < today;
  }

  isExpiringSoon(dateString: string): boolean {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const today = new Date();

    // if already expired, it's not "expiring soon"
    if (expiryDate < today) return false;

    // check if expiring within 30 days
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    return expiryDate.getTime() - today.getTime() < thirtyDaysInMs;
  }
}
